import React, { useContext, useRef } from "react";
import Button from "../Components/Button";
import ReportsLayout from "./ReportsLayout";
import useSalesSummary from "../hooks/useSalesSummary";
import { FilterContext } from "../FilterContextProvider";
import ReactToPrint from "react-to-print";
import Select from "react-select";
import useDateSelections from "../hooks/useDateSelections";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { exportToCSV } from "../Utility/Csv";

const InventoryValue = () => {
  const { filterData } = useContext(FilterContext);
  const { productsToRender, resultsLoading, loadError } = useSalesSummary("active");
  const printRef = useRef();
  const { dateOptions, handleDateSelect, renderCustomDateSelector } =
    useDateSelections();

  const totalValue = productsToRender
    .filter((prod) => prod?.sold !== undefined)
    .reduce((a, b) => {
      return a + b.adjustableEndDateCurrent * b.cost;
    }, 0);

  const productSales = productsToRender.map((product) => ({
    Name: product.name,
    Size: product.size?.name,
    Category: product.category.name,
    Current: product.adjustableEndDateCurrent,
    Cost: product.cost,
    Value: product.adjustableEndDateCurrent * product.cost,
  }));

  const totals = {
    Name: "Totals",
    Size: "",
    Category: "",
    Current: "",
    Cost: "",
    Value: totalValue,
  };

  const combined = productSales.concat(totals);

  return (
    <ReportsLayout>
      <div className="mt-8 mx-8 py-10 px-12 bg-white rounded-lg border">
        {productsToRender.length > 0 && (
          <div className="flex justify-end text-sm">
            <ReactToPrint
              trigger={() => <Button className="button" text="Print"></Button>}
              content={() => printRef.current}
            />
            <Button
              className="button button-white ml-2"
              text="Export"
              onClick={() =>
                exportToCSV(
                  combined,
                  `${filterData.artist_name}_inventory_value`
                )
              }
            ></Button>
          </div>
        )}
        <div className="max-w-max">
          <Link to="/reports">
            <div className="-mt-6 mb-6 font-medium text-black text-sm">
              <FontAwesomeIcon icon={faArrowLeft}></FontAwesomeIcon> Back to
              reports
            </div>
          </Link>
        </div>
        <div className="flex flex-col text-sm w-1/4">
          <span className="text-xs">SELECT DATE RANGE</span>
          <Select
            id="date"
            className="text-xs font-light w-full"
            placeholder="Select date range"
            options={dateOptions}
            onChange={handleDateSelect}
            value={dateOptions?.filter(
              (each) => each.value === filterData.date_button
            )}
          />
        </div>
        <div className="my-4 w-1/2">
          {filterData.date_button === "custom" && renderCustomDateSelector()}
        </div>
        <div ref={printRef} className="mt-8 mx-8 text-black">
          <div className="flex text-lg font-bold mb-2">
            <div>
              <div className="text-xl font-extrabold">
                TM Inventory Value Report
              </div>
              <div>{filterData.artist_name}</div>
            </div>
            <div className="ml-32 text-base font-normal italic">
              <div>End Date:</div>
              <div>Total Value:</div>
            </div>
            <div className="ml-2 text-base font-normal italic">
              <div>
                {filterData.end_date?.slice(5, 7)}/
                {filterData.end_date?.slice(8, 10)}/
                {filterData.end_date?.slice(0, 4)}
              </div>
              <div>
                {totalValue?.toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                })}
              </div>
            </div>
          </div>
          {loadError ? (
            <div className="w-full flex justify-center py-16 text-red-500">
              Something went wrong. Please refresh the page and try again.
            </div>
          ) : resultsLoading ? (
            <div className="w-full flex justify-center py-16">
              <FontAwesomeIcon icon={faSpinner} spin size="2x" />
            </div>
          ) : (
          <table className="w-full">
            <thead className="text-xs font-light">
              <tr className="text-right h-12">
                <td className="w-1/4 text-left">PRODUCT</td>
                <td className="w-1/25 text-left">SIZE</td>
                <td className="w-1/6">CURRENT</td>
                <td className="w-1/6">COST</td>
                <td className="w-1/6">INVENTORY VALUE</td>
              </tr>
            </thead>
            <tbody>
              {productsToRender
                .filter((prod) => prod.sold !== undefined)
                .map((productToRender, i) => {
                  return (
                    <tr
                      key={productToRender.id}
                      className="text-sm text-right h-7"
                    >
                      <td id={`product_name_${i}`} className="text-left">
                        {productToRender.name}
                      </td>
                      <td id={`size_${i}`} className="text-left">
                        {productToRender.product_size}
                      </td>
                      <td id={`current_${i}`}>
                        {productToRender.adjustableEndDateCurrent}
                      </td>
                      <td id={`cost_${i}`}>
                        ${productToRender.cost?.toFixed(2)}
                      </td>
                      <td id={`value_${i}`}>
                        $
                        {(
                          productToRender.adjustableEndDateCurrent *
                          productToRender.cost
                        )?.toFixed(2)}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
          )}
        </div>
      </div>
    </ReportsLayout>
  );
};

export default InventoryValue;
